import { Link, usePage } from "@inertiajs/react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

interface TabItem {
  title: string
  href: string
  icon: LucideIcon
}

interface BottomTabBarProps {
  tabs: TabItem[]
  className?: string
}

export function BottomTabBar({ tabs, className }: BottomTabBarProps) {
  const { url } = usePage()

  return (
    <nav
      aria-label="Main navigation"
      className={cn("flex place-content-between items-center gap-6", className)}
      style={{
        height: 56,
        borderRadius: 9999,
        padding: 6,
        background: "var(--fig-nav-pill-bg)",
        boxShadow: "var(--fig-nav-pill-shadow)",
      }}
    >
      {tabs.map((tab) => {
        const isActive = url === tab.href || url.startsWith(tab.href + "/")
        const Icon = tab.icon

        return (
          <Link
            key={tab.href}
            href={tab.href}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "flex flex-col items-center justify-center",
              !isActive && "tab-inactive",
            )}
            style={{
              height: 44,
              paddingLeft: 18,
              paddingRight: 18,
              borderRadius: 22,
              transition:
                "background 150ms ease, color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
              ...(isActive
                ? {
                    background: "var(--fig-active-tab-bg)",
                    boxShadow: "var(--fig-active-tab-shadow)",
                    color: "var(--fig-text-active)",
                    fontWeight: 600,
                  }
                : {
                    color: "var(--fig-text-inactive)",
                    fontWeight: 500,
                  }),
            }}
          >
            <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
            <span style={{ fontSize: 11, lineHeight: "14px", marginTop: 2 }}>
              {tab.title}
            </span>
          </Link>
        )
      })}
    </nav>
  )
}
